import { fetcher } from '@data/utils/api/fetcher/fetcher';
import formatDate from '@data/utils/format-date';
import { useQuery } from '@tanstack/react-query';
import {
  metricsTableV2Schema,
  type MetricsTableOrderBy,
  type MetricsTableV2Response,
} from '@data/hooks/api/use-metrics-table-v2-api/use-metrics-table-v2-api';

/** Admin tables roll up by organization, so they carry `organizationIds`. */
export enum AdminMetricTableType {
  /** Every organization in scope, a row each. */
  AllOrganizationOverview = 'ALL_ORGANIZATION_OVERVIEW',
  /** One organization's educators, a row each — the drill-down. */
  IndividualOrganizationOverview = 'INDIVIDUAL_ORGANIZATION_OVERVIEW',
}

export interface AdminMetricsTableRequest {
  metricType: AdminMetricTableType;
  /** Every organization in scope, for the all-organizations table. */
  organizationIds?: string[];
  /** The one organization being read; required by the drill-down table. */
  orgId?: string;
  /** Narrows the drill-down to these educators; omitted means all of them. */
  teacherIds?: string[];
  edApps: string[];
  startDate: Date;
  endDate: Date;
  page: number;
  pageSize: number;
  searchTerms?: string;
  orderBy?: MetricsTableOrderBy[];
  fetchAllRecords?: boolean;
}

export interface UseAdminMetricsTableApiProps extends AdminMetricsTableRequest {
  enabled?: boolean;
  /**
   * Shapes the response before anything reads it. Kept out of the request, so
   * it reaches neither the query key nor the body — and so the on-screen table
   * and the CSV export, which share these arguments, cannot disagree.
   */
  select?: (table: MetricsTableV2Response) => MetricsTableV2Response;
}

function buildBody({
  metricType,
  organizationIds,
  orgId,
  teacherIds,
  edApps,
  startDate,
  endDate,
  page,
  pageSize,
  searchTerms,
  orderBy,
  fetchAllRecords = false,
}: AdminMetricsTableRequest) {
  return {
    metricType,
    page,
    pageSize,
    fetchAllRecords,
    startDate: formatDate(startDate),
    endDate: formatDate(endDate, { boundary: 'end' }),
    edApps,
    // The two tables scope differently: one by a list of organizations, the
    // other by the single organization being read.
    ...(organizationIds ? { organizationIds } : {}),
    ...(orgId ? { orgId } : {}),
    ...(teacherIds && teacherIds.length > 0 ? { teacherIds } : {}),
    searchTerms: searchTerms ?? null,
    orderBy: orderBy ?? null,
  };
}

const getAdminMetricsTableApi = async (request: AdminMetricsTableRequest) =>
  fetcher(`/metric-table/admin/${request.metricType}`, {
    service: '/data',
    version: '/v2',
    method: 'POST',
    body: JSON.stringify(buildBody(request)),
  }).then((res) => metricsTableV2Schema.parse(res));

export function adminMetricsTableQueryKey(request: AdminMetricsTableRequest) {
  return ['admin-metrics-table', buildBody(request)] as const;
}

/** The Details table on the admin Overview, for every organization in scope. */
export function useAdminMetricsTableApi({
  enabled,
  select,
  ...request
}: UseAdminMetricsTableApiProps) {
  const query = useQuery({
    queryKey: adminMetricsTableQueryKey(request),
    queryFn: () => getAdminMetricsTableApi(request),
    select,
    // Nothing in scope: skipped rather than sent with an empty scope. The
    // drill-down scopes by one organization, the other table by a list.
    enabled:
      enabled !== false &&
      request.edApps.length > 0 &&
      (request.orgId !== undefined ||
        (request.organizationIds?.length ?? 0) > 0),
    staleTime: 3 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return { ...query, loading: query.isLoading, error: query.error };
}
